import React, { useRef, useState } from 'react';
import { ExternalLink, Headset, Layers, MessageCircle } from 'lucide-react';
import { PromotionPresentationData } from '../../types/promotion';
import { SystemSettings } from '../../types/systemSettings';
import { usePromotionPresentation } from '../../hooks/usePromotionPresentation';
import PromotionSidebar from '../admin/PromotionSidebar';
import PromotionBottomSheet, { promotionFaqItems } from '../admin/PromotionBottomSheet';
import InactivePromotionNotice from '../common/InactivePromotionNotice';
import InactivePromotionContact from './InactivePromotionContact';
import LazyImage from '../common/LazyImage';
import PromotionHtml, { PromotionTextSize } from './PromotionHtml';
import CharacterImage from '../common/CharacterImage';
import PromotionScrollTop from './PromotionScrollTop';
import PromotionLinkShare from './PromotionLinkShare';
import { buildSharingMetadata, PUBLIC_PROMOTION_ORIGIN } from '../../config/sharingPolicy.mjs';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import ConsultantContactDialog, { visibleContactProfile } from './ConsultantContactDialog';

interface PromotionExperienceProps {
  promotion: PromotionPresentationData;
  hideElements?: string | null;
  systemSettings?: Partial<SystemSettings> | null;
  textSize?: PromotionTextSize;
  textControls?: React.ReactNode;
}

function Section({ title, children, className = '' }: { title: string; children: React.ReactNode; className?: string }) {
  return <section className={`promotion-card ${className}`}><h2 className="promotion-section-title">{title}</h2>{children}</section>;
}

export default function PromotionExperience({ promotion, hideElements, systemSettings: propSystemSettings, textSize = 'normal', textControls }: PromotionExperienceProps) {
  const [openMenu, setOpenMenu] = useState<'products' | 'faq' | 'contact' | null>(null);
  const contactButtonRef = useRef<HTMLButtonElement>(null);
  const view = usePromotionPresentation(promotion, hideElements, propSystemSettings);
  const visible = (name: string) => !view.hiddenElements.includes(name);
  const siteName = view.systemSettings?.siteName?.trim() || '렌탈프로모션';
  const benefitLabel = `${siteName}만의 혜택`;
  const contactProfile = visibleContactProfile(view.systemSettings?.contactProfile);

  return (
    <article className={`promotion-document${promotion.isActive ? '' : ' promotion-inactive'}`}>
      {(!promotion.isActive || visible('title')) && (
        <header className={`promotion-header${!promotion.isActive && promotion.target ? ' promotion-header--with-target' : ''}`}>
          <div className="promotion-header-content">
            <div className="promotion-meta">
              <span>{view.formatMonth(promotion.month)}</span>
              <span className={`promotion-status${promotion.isActive ? '' : ' promotion-status--inactive'}`}>{promotion.isActive ? '활성' : '종료'}</span>
            </div>
            <h1 title={promotion.title}>{promotion.title}</h1>
          </div>
          {promotion.isActive && <div className="promotion-meta promotion-header-details"><code>{promotion.code}</code><span title={benefitLabel}>{benefitLabel}</span></div>}
          {!promotion.isActive && <div className="promotion-meta promotion-header-details"><span className="promotion-inactive-brand">{siteName}</span></div>}
          <CharacterImage className="promotion-header-art" theme={view.systemSettings?.characterTheme} slot={promotion.isActive ? 'titleActive' : 'titleInactive'} />
        </header>
      )}
      <div className="promotion-tools">
      {textControls}
      <PromotionLinkShare title={promotion.title} url={buildSharingMetadata({
        origin: PUBLIC_PROMOTION_ORIGIN,
        identifier: promotion.slug || window.location.pathname.replace(/^\/+/, ''),
        promotion,
      }).url} />
      </div>
      <div className="promotion-sections">
        {(!promotion.isActive || visible('target')) && promotion.target && <Section title="타겟 고객그룹" className={promotion.isActive ? '' : 'promotion-inactive-target'}><p className="promotion-target">{promotion.target}</p></Section>}
        {visible('greeting') && promotion.isActive && <Section title="인사말"><PromotionHtml kind="greeting" content={promotion.greeting || ''} textSize={textSize} /></Section>}
        {promotion.isActive && <Section title="프로모션 내용" className="promotion-body-card"><PromotionHtml content={promotion.content || ''} textSize={textSize} /></Section>}
        {visible('closing') && promotion.isActive && <Section title="매듭말" className="promotion-closing"><PromotionHtml kind="greeting" content={promotion.closing || ''} textSize={textSize} /><CharacterImage className="promotion-closing-art" theme={view.systemSettings?.characterTheme} slot="closingRemark" /></Section>}
        {view.hasOtherProducts && visible('otherProducts') && promotion.isActive && (
          <Section title="다른 기능 제품 더 보기">
            <p className="promotion-muted">아래 링크를 클릭하면 다른 기능 제품 혜택도 바로 확인할 수 있습니다.</p>
            <div className={`promotion-related-list prose-${textSize}`}>
              {view.isLoadingOtherProducts ? <p className="promotion-muted">다른제품 정보를 불러오는 중...</p> : Object.values(view.otherProducts).map(product => (
                <button key={product.id} type="button" className="promotion-related-link" onClick={() => view.handleOtherProductClick(product.id)}>
                  <span><small>{product.code}</small>{product.title}</span><span aria-hidden="true">↗</span>
                </button>
              ))}
            </div>
          </Section>
        )}
        {!promotion.isActive && <InactivePromotionNotice systemSettings={view.systemSettings} />}
        {!promotion.isActive && <InactivePromotionContact contact={promotion.contact} />}
        {promotion.isActive && visible('contact') && (
          <Section title="연락처">
            <div className="promotion-contact">
              {promotion.contact.split('\n').map((line, index) => {
                const trimmedLine = line.trim();
                if (!trimmedLine) return null;
                const urlPattern = /(https?:\/\/[^\s]+)/g;
                if (urlPattern.test(trimmedLine)) {
                  return <div key={index}>{trimmedLine.split(urlPattern).map((part, partIndex) => urlPattern.test(part) ? (
                    <a key={partIndex} href={part} target="_blank" rel="noopener noreferrer" onClick={event => { event.preventDefault(); window.open(part, '_blank', 'noopener,noreferrer'); }}>{part}</a>
                  ) : part)}</div>;
                }
                return <div key={index}>{trimmedLine}</div>;
              })}
            </div>
          </Section>
        )}
        {promotion.isActive && promotion.imageUrl && <Section title="첨부 이미지"><div className="promotion-image-surface"><LazyImage src={promotion.imageUrl} alt="프로모션 이미지" className="w-full h-auto object-contain" /></div></Section>}
      </div>
      {!promotion.isActive && <footer className="promotion-inactive-footer">{siteName} · 프로모션 안내</footer>}
      {promotion.isActive && <>
      <nav className="promotion-navigation promotion-mobile-navigation" aria-label="프로모션 메뉴">
        <div className={`promotion-navigation-inner${contactProfile ? '' : ' promotion-navigation-inner--without-contact'}`}>
          {contactProfile && <button ref={contactButtonRef} type="button" aria-haspopup="dialog" aria-expanded={openMenu === 'contact'} onClick={() => setOpenMenu('contact')}><Headset size={19} aria-hidden="true" />상담원 연락처</button>}
          <Popover open={openMenu === 'products'} onOpenChange={open => setOpenMenu(open ? 'products' : null)}>
            <PopoverTrigger asChild><button type="button" disabled={!visible('otherProducts') || view.isLoadingOtherProducts || !view.hasOtherProducts}><Layers size={19} aria-hidden="true" />제품 더 보기</button></PopoverTrigger>
            <PopoverContent side="top" collisionPadding={12} className="promotion-theme promotion-submenu" aria-label="제품 선택" onOpenAutoFocus={event => event.preventDefault()} onCloseAutoFocus={event => event.preventDefault()}>
              {Object.values(view.otherProducts).map(product => <button key={product.id} type="button" onClick={() => { setOpenMenu(null); view.handleOtherProductClick(product.id); }}>{product.title}</button>)}
            </PopoverContent>
          </Popover>
          <Popover open={openMenu === 'faq'} onOpenChange={open => setOpenMenu(open ? 'faq' : null)}>
            <PopoverTrigger asChild><button type="button"><MessageCircle size={19} aria-hidden="true" />자주 묻는 질문</button></PopoverTrigger>
            <PopoverContent side="top" align="end" collisionPadding={12} className="promotion-theme promotion-submenu" aria-label="질문 선택" onOpenAutoFocus={event => event.preventDefault()} onCloseAutoFocus={event => event.preventDefault()}>
              {promotionFaqItems.map(item => <button key={item.id} type="button" onClick={() => { setOpenMenu(null); view.setSelectedMenu(item.id); view.setIsDialogOpen(true); }}>{item.title}</button>)}
              <div className="promotion-submenu-separator" />
              <a className="promotion-submenu-link" href="https://rentaltalk.kr/faq?c=1" target="_blank" rel="noopener noreferrer">자주 묻는 질문 모음 보기 <ExternalLink size={15} aria-hidden="true" /></a>
            </PopoverContent>
          </Popover>
        </div>
      </nav>
      {contactProfile && <ConsultantContactDialog open={openMenu === 'contact'} onOpenChange={open => setOpenMenu(open ? 'contact' : null)} profile={contactProfile} returnFocus={contactButtonRef} />}
      <PromotionBottomSheet isOpen={view.isDialogOpen} onClose={() => view.setIsDialogOpen(false)} selectedMenu={view.selectedMenu} textSize={textSize} />
      <PromotionSidebar isOpen={view.isSlidePanelOpen} onClose={view.handleCloseSlidePanel} selectedProductCode={view.selectedProductCode} textSize={textSize} /></>}
      <PromotionScrollTop obscured={view.isDialogOpen || view.isSlidePanelOpen || openMenu !== null} />
    </article>
  );
}
